import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { map } from "rxjs/operators";
import { StorageService } from "../../../services/storage.service";
import { Uris } from "../../../services/Uris";
import { Observable } from "rxjs";
import {
  cycle,
  planting,
  pond,
  submitCycle,
  objectPlanting,
  submitHarvest,
  harvest,
  submitPetitionPaginationHarvest,
  submitPetitionPaginationCycle,
  patchcycle,
  Planting,
  pondPlanting
} from "../interfaces/cycle.interface";
import moment from "moment";
import { GlobalFunction } from "../../../services/globalFuntion";

@Injectable({
  providedIn: "root"
})
export class CyclesService {
  constructor(
    public storageService: StorageService,
    private http: HttpClient,
    public router: Router,
    public globalFunction: GlobalFunction
  ) {}
  createCycle(body: cycle): Observable<submitCycle> {
    return this.http.post<submitCycle>(Uris.CYCLES, body, this.storageService.headers()).pipe(
      map((resp: submitCycle) => {
        return resp;
      })
    );
  }

  getCycles(query: string = ""): Observable<submitCycle[]> {
    return this.http
      .get<submitCycle[]>(Uris.CYCLES + query, this.storageService.headers(false))
      .pipe(
        map((resp: submitCycle[]) => {
          return resp;
        })
      );
  }
  /**
   * Obtiene los ciclos con el paginado del servidor.
   * @param query - Query string ya construido (page, page_size, farm_id, status, ordering).
   * @returns Observable con la respuesta paginada de ciclos.
   */
  getCyclesPaginated(query: string = ""): Observable<submitPetitionPaginationCycle> {
    return this.http
      .get<submitPetitionPaginationCycle>(Uris.CYCLES + query, this.storageService.headers())
      .pipe(
        map((resp: submitPetitionPaginationCycle) => {
          return resp;
        })
      );
  }

  getPlantingMissingOldCyclesHarvest(query: string = ""): Observable<pondPlanting[]> {
    return this.http
      .get<pondPlanting[]>(
        Uris.PLANTINGS + "missing_old_cycles_harvests/" + query,
        this.storageService.headers()
      )
      .pipe(
        map((resp: any) => {
          return resp;
        })
      );
  }
  getCycleById(id: number): Observable<submitCycle> {
    return this.http.get<submitCycle>(Uris.CYCLES + id, this.storageService.headers()).pipe(
      map((resp: submitCycle) => {
        return resp;
      })
    );
  }

  getCycleDeadlineStatus(activeCycles: submitCycle[]): 3 | 1 {
    const latestEndDate = activeCycles.reduce((latest, cycle) => {
      const current = new Date(cycle.end_date);
      return current > latest ? current : latest;
    }, new Date(activeCycles[0].end_date));

    const today = new Date();

    const todayStr = today.toISOString().split("T")[0];
    const latestStr = latestEndDate.toISOString().split("T")[0];

    // Se alcanzó o pasó la fecha límite del ciclo activo
    return latestStr <= todayStr ? 3 : 1;
  }

  validateCycleStatus(data_cycles: submitCycle[]): 1 | 2 | 3 | 4 | 5 {
    /*
    1.- Todo normal
    2.- Cuando no haya ningún ciclo activo
    3.- Cuando Se haya llegado a fecha limite del ciclo activo más longevo.
    4.- Un ciclo creado pero no Iniciado
    5.- Solo un ciclo activo y todos los estanques cosechados */
    let cycles = data_cycles.map((cycle) => {
      const progress = this.globalFunction.calculateCycleProgress(cycle);
      return {
        ...cycle,
        ...progress
      };
    });

    if (!cycles || cycles.length === 0) return 2;

    const activeCycles = cycles.filter((cycle) => cycle.status === 1);

    if (activeCycles.length === 0) {
      const existCycle = cycles.filter((cycle) => cycle.status === 0);
      if (existCycle) return 4;
      return 1;
    }
    if (activeCycles.length === 1) {
      if (
        activeCycles[0].participant_ponds.length > 0 &&
        activeCycles[0].harvested_ponds.length > 0
      ) {
        if (activeCycles[0].participant_ponds.length === activeCycles[0].harvested_ponds.length) {
          return 5;
        }
      }
    }

    const all_100_percent = cycles.every((item) => Number(item.progressPercentage) === 100);
    if (all_100_percent) {
      return 3;
    }
    return this.getCycleDeadlineStatus(activeCycles);
  }

  async getCurrentStatusCycles(
    farm_id: number
  ): Promise<{ cycles: submitCycle[]; status: 1 | 2 | 3 | 4 | 5 }> {
    const fields = [
      "id",
      "name",
      "start_date",
      "end_date",
      "status",
      "participant_ponds",
      "harvested_ponds"
    ];

    try {
      const object_query = {
        fields: fields.join(","),
        farm_id: farm_id,
        current_cycles: "true"
      };
      const query = this.globalFunction.constructQuery(object_query);
      const data = await this.getCycles(query).toPromise();

      if (data) {
        return { cycles: data, status: this.validateCycleStatus(data) };
      }
    } catch (error) {
      console.error("Error fetching cycles:", error);
    }

    return { cycles: [], status: 1 };
  }

  patchCycle(id: number, body: patchcycle): Observable<submitCycle> {
    return this.http
      .patch<submitCycle>(`${Uris.CYCLES}${id}/`, body, this.storageService.headers())
      .pipe(
        map((resp: submitCycle) => {
          return resp;
        })
      );
  }

  closeCycle(id: number): Observable<submitCycle> {
    return this.http
      .post<submitCycle>(`${Uris.CYCLES}${id}/close-cycle/`, {}, this.storageService.headers())
      .pipe(
        map((resp: submitCycle) => {
          return resp;
        })
      );
  }
  startCycle(id: number): Observable<submitCycle> {
    return this.http
      .post<submitCycle>(`${Uris.CYCLES}${id}/start-cycle/`, {}, this.storageService.headers())
      .pipe(
        map((resp: submitCycle) => {
          return resp;
        })
      );
  }

  getPonds(query: string = ""): Observable<pond[]> {
    return this.http.get<pond[]>(`${Uris.PONDS}${query}`, this.storageService.headers()).pipe(
      map((resp: any) => {
        return resp;
      })
    );
  }
  getPondsById(id: number, query: string = ""): Observable<pond> {
    return this.http.get<pond>(`${Uris.PONDS + id}/` + query, this.storageService.headers()).pipe(
      map((resp: any) => {
        return resp;
      })
    );
  }
  removePonds(cycle_id: number, body: { ponds: Array<number> }) {
    return this.http
      .post<pond>(`${Uris.CYCLES + cycle_id}/remove-ponds/`, body, this.storageService.headers())
      .pipe(
        map((resp: any) => {
          return resp;
        })
      );
  }
  getPlanting(query: string = ""): Observable<planting[]> {
    return this.http
      .get<planting[]>(`${Uris.PLANTINGS}` + query, this.storageService.headers())
      .pipe(
        map((resp: any) => {
          return resp;
        })
      );
  }

  createPlanting(value: objectPlanting): Observable<Planting[]> {
    return this.http.post<Planting[]>(Uris.PLANTINGS, value, this.storageService.headers()).pipe(
      map((resp: any) => {
        return resp;
      })
    );
  }
  updatePlanting(id: number, value: objectPlanting): Observable<planting> {
    return this.http
      .patch<objectPlanting>(Uris.PLANTINGS + id + "/", value, this.storageService.headers())
      .pipe(
        map((resp: any) => {
          return resp;
        })
      );
  }

  harvestPlanting(value: harvest): Observable<submitHarvest> {
    return this.http.post<submitHarvest>(Uris.HARVEST, value, this.storageService.headers()).pipe(
      map((resp) => {
        return resp;
      })
    );
  }
  getHarvest(query: string): Observable<submitPetitionPaginationHarvest> {
    return this.http
      .get<submitPetitionPaginationHarvest>(Uris.HARVEST + query, this.storageService.headers())
      .pipe(
        map((resp) => {
          return resp;
        })
      );
  }
  updateHarvest(id: number, body: harvest): Observable<harvest> {
    return this.http
      .patch<harvest>(`${Uris.HARVEST + id}/`, body, this.storageService.headers())
      .pipe(
        map((resp) => {
          return resp;
        })
      );
  }
  deleteHarvest(id: number): Observable<harvest> {
    return this.http.delete<harvest>(Uris.HARVEST + `${id}/`, this.storageService.headers()).pipe(
      map((resp) => {
        return resp;
      })
    );
  }

  validateClosedCycle = (current_cycle: submitCycle | null): boolean => {
    if (!current_cycle) return false;

    const { participant_ponds, planted_ponds, harvested_ponds, start_date, end_date, status } =
      current_cycle;

    if (!status) return false;

    /* Un ciclo activo puede quedarse sin estanques participantes si estos se
       eliminaron, deshabilitaron o se movieron a otro ciclo. Sin estanques no
       hay nada que sembrar ni cosechar, por lo que la unica salida es cerrarlo
       sin esperar a que venza la fecha de fin. */
    if (status === 1 && (!participant_ponds || participant_ponds.length === 0)) return true;

    const today = moment().format("YYYY-MM-DD");

    const withinRange = this.globalFunction.areDatesWithinRange({
      rangeStart: start_date,
      rangeEnd: end_date,
      dateStart: today,
      dateEnd: today
    });

    if (harvested_ponds.length > 0) return true;
    if (status === 4) return true;
    if (status == 1) {
      if (!withinRange) return true;
    }

    if (planted_ponds.length > 0) {
      return planted_ponds.length === participant_ponds.length;
    }

    return false;
  };

  getPlantingByPondId = async (pond_id: number, cycle_id: number | null) => {
    try {
      const data = await this.getPlanting(
        `?pond_id=${pond_id}&ordering=-date&cycle_id=${cycle_id}`
      ).toPromise();

      return data && data.length > 0 ? data[0] : null;
    } catch (error) {
      console.error(`Error fetching planting for pond ${pond_id}:`, error);
      return null;
    }
  };

  setPlantingPonds = async (ponds: pond[], cycle_id: number): Promise<pond[]> => {
    const results = await Promise.all(
      ponds.map(async (item) => {
        if (cycle_id) {
          const planting = await this.getPlantingByPondId(item.id, cycle_id);
          return {
            ...item,
            planting,
            checked: false,
            is_ready: false,
            is_finished: false,
            is_loading: false
          };
        }
        return item;
      })
    );
    return results;
  };
}
